/* =========================================================
   Kaiju — carousels (drag scroll + videos)
   - athletes: native scroll-snap + inline video play (desktop+mobile videos rendered)
   - committee: native scroll-snap + inline video play + poster restore
   - ingredients: drag scroll only
   ========================================================= */

(() => {
  const DEBUG = false;
  const log = (...a) => DEBUG && console.log("[kaiju]", ...a);

  // Drag threshold (px) before we consider it a drag (and block click)
  const THRESH = 6;

  function attachDragScroll(track, opts = {}) {
    const clickGuardMode = opts.clickGuardMode || "all"; // "all" | "links"
    const ignoreInteractive = opts.ignoreInteractive ?? false;

    let isDown = false;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let dragged = false;
    let didMove = false;

    // IMPORTANT: allow horizontal pan for carousels
    // (avoid conflicting with CSS `touch-action` if you can)
    track.style.touchAction = "pan-x pinch-zoom";

    const isInteractiveTarget = (target) =>
      !!target.closest("a,button,input,textarea,select,label,summary,video,[role='button']");

    track.addEventListener("pointerdown", (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;

      // If user starts on an interactive element (play button/video/link),
      // do NOT arm dragging (prevents click from being killed on desktop).
      if (ignoreInteractive && isInteractiveTarget(e.target)) return;

      dragged = false;
      didMove = false;

      isDown = true;
      startX = e.clientX;
      startY = e.clientY;
      startScrollLeft = track.scrollLeft;

      track.setPointerCapture?.(e.pointerId);
      track.classList.add("is-dragging");
    });

    track.addEventListener(
      "pointermove",
      (e) => {
        if (!isDown) return;

        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        // Prevent page scroll only when movement is mainly horizontal
        if (Math.abs(dx) > Math.abs(dy)) e.preventDefault();

        didMove = true;
        if (!dragged && (Math.abs(dx) > THRESH || Math.abs(dy) > THRESH)) dragged = true;

        track.scrollLeft = startScrollLeft - dx;
      },
      { passive: false }
    );

    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      track.classList.remove("is-dragging");

      // Keep "dragged" true just long enough so click gets ignored
      if (dragged && didMove) setTimeout(() => (dragged = false), 120);
      else dragged = false;

      didMove = false;
    };

    track.addEventListener("pointerup", endDrag);
    track.addEventListener("pointercancel", endDrag);
    track.addEventListener("pointerleave", endDrag);

    // Click guard: if we dragged, block click (capture phase)
    track.addEventListener(
      "click",
      (e) => {
        if (!dragged) return;

        // If we are ignoring interactive targets, let them through
        // (in practice, with ignoreInteractive:true, dragged won't arm from these anyway)
        if (ignoreInteractive && isInteractiveTarget(e.target)) return;

        if (clickGuardMode === "links") {
          const aOrBtn = e.target.closest("a, button");
          if (!aOrBtn) return;
        }

        e.preventDefault();
        e.stopPropagation();
      },
      true
    );

    return { getDragged: () => dragged };
  }

  // -------------------------------------------------------
  // Helpers video (sound + force first frame)
  // -------------------------------------------------------
  const stashPoster = (video) => {
    if (!video || video.dataset.kPosterStashed === "1") return;
    const p = video.getAttribute("poster");
    if (p) video.dataset.kPoster = p;
    video.dataset.kPosterStashed = "1";
  };

  const removePosterAttr = (video) => {
    if (!video) return;
    stashPoster(video);
    video.removeAttribute("poster");
  };

  const restorePosterAttr = (video) => {
    if (!video) return;
    const p = video.dataset.kPoster;
    if (p) video.setAttribute("poster", p);
  };

  // Make sure user can HEAR audio when play is user-initiated
  const enableSound = (video) => {
    if (!video) return;
    video.muted = false;
    video.removeAttribute("muted");
    try {
      video.volume = 1;
    } catch {}
    video.playsInline = true;
  };

  // Helps Safari/Chrome render first frame (hide poster reliably)
  const forceFirstFrameSoon = (video) => {
    if (!video) return;

    const kick = () => {
      try {
        if (video.currentTime === 0) video.currentTime = 0.01;
      } catch {}
    };

    if (video.readyState >= 1) kick();
    else video.addEventListener("loadedmetadata", kick, { once: true });
  };

  const resetToPoster = (video) => {
    if (!video) return;
    try {
      video.pause();
      video.currentTime = 0;
      video.load?.();
    } catch {}
  };

  // -----------------------------
  // Athletes (desktop click fix)
  // -----------------------------
  function attachAthletesVideo(carouselRoot, track, dragState) {
    const WRAP_SEL = "[data-k-athletes-video-wrap]";
    const VIDEO_SEL = "video[data-k-athletes-video]";
    const PLAY_SEL = "[data-k-athletes-play]";
    const PLAYING_CLASS = "k-athletes__video-wrap--playing";

    const pauseAll = () => {
      track.querySelectorAll(WRAP_SEL).forEach((wrap) => wrap.classList.remove(PLAYING_CLASS));
      track.querySelectorAll(VIDEO_SEL).forEach((v) => {
        try {
          v.pause();
        } catch {}
      });
    };

    const playOne = (wrap, video) => {
      pauseAll();
      wrap.classList.add(PLAYING_CLASS);

      enableSound(video);
      try {
        video.preload = "auto";
      } catch {}

      const p = video.play();
      if (p && typeof p.catch === "function") {
        p.catch((err) => {
          log("athletes play blocked", err);
          wrap.classList.remove(PLAYING_CLASS);
        });
      }
    };

    const pauseOne = (wrap, video) => {
      try {
        video.pause();
      } catch {}
      wrap.classList.remove(PLAYING_CLASS);
    };

    track.addEventListener("click", (e) => {
      // If we dragged, do nothing
      if (dragState.getDragged()) return;

      const wrap = e.target.closest(WRAP_SEL);
      if (!wrap) return;

      const video = wrap.querySelector(VIDEO_SEL);
      if (!video) return;

      const clickedPlayBtn = !!e.target.closest(PLAY_SEL);
      const clickedVideo = e.target.closest(VIDEO_SEL) === video;
      if (!clickedPlayBtn && !clickedVideo) return;

      const isPlaying = !video.paused && !video.ended;
      if (isPlaying) pauseOne(wrap, video);
      else playOne(wrap, video);
    });

    const io = new IntersectionObserver(
      (entries) => entries.forEach((entry) => !entry.isIntersecting && pauseAll()),
      { threshold: 0.01 }
    );
    io.observe(carouselRoot);
  }

  // -----------------------------
  // Committee
  // -----------------------------
  function attachCommitteeVideo(sectionRoot, track, dragState) {
    const CARD_SEL = "[data-k-committee-card]";
    const VIDEO_SEL = "video[data-k-committee-video]";
    const BTN_SEL = "[data-k-committee-play]";
    const PLAYING_CLASS = "is-playing";

    const mq = window.matchMedia("(max-width: 989px)");

    const getActiveVideoForCard = (card) => {
      const isMobile = mq.matches;
      const vMobile = card.querySelector("video.k-committee__video--mobile[data-k-committee-video]");
      const vDesk = card.querySelector("video.k-committee__video--desktop[data-k-committee-video]");
      return (isMobile ? (vMobile || vDesk) : (vDesk || vMobile)) || null;
    };

    const setCardPlaying = (card, isPlaying) => {
      if (!card) return;
      card.classList.toggle(PLAYING_CLASS, !!isPlaying);
    };

    const pauseAll = () => {
      track.querySelectorAll(CARD_SEL).forEach((card) => setCardPlaying(card, false));
      track.querySelectorAll(VIDEO_SEL).forEach((v) => {
        restorePosterAttr(v);
        resetToPoster(v);
      });
    };

    const playCard = (card) => {
      const video = getActiveVideoForCard(card);
      if (!video) return;

      pauseAll();

      stashPoster(video);
      removePosterAttr(video);
      forceFirstFrameSoon(video);

      enableSound(video);
      try {
        video.preload = "auto";
      } catch {}

      const p = video.play();
      if (p && typeof p.catch === "function") {
        p.catch((err) => {
          log("committee play blocked", err);
          restorePosterAttr(video);
          setCardPlaying(card, false);
          resetToPoster(video);
        });
      }
    };

    const pauseCard = (card) => {
      const video = getActiveVideoForCard(card);
      if (video) {
        restorePosterAttr(video);
        resetToPoster(video);
      }
      setCardPlaying(card, false);
    };

    track.addEventListener("click", (e) => {
      if (dragState.getDragged()) return;

      const card = e.target.closest(CARD_SEL);
      if (!card) return;

      const clickedBtn = !!e.target.closest(BTN_SEL);
      const clickedVideo = !!e.target.closest(VIDEO_SEL);
      if (!clickedBtn && !clickedVideo) return;

      const video = getActiveVideoForCard(card);
      if (!video) return;

      const isPlaying = !video.paused && !video.ended;
      if (isPlaying) pauseCard(card);
      else playCard(card);
    });

    // Sync UI with real playback state
    track.querySelectorAll(VIDEO_SEL).forEach((video) => {
      stashPoster(video);

      video.addEventListener("playing", () => {
        const card = video.closest(CARD_SEL);
        setCardPlaying(card, true);
      });

      video.addEventListener("pause", () => {
        const card = video.closest(CARD_SEL);
        setCardPlaying(card, false);
      });

      video.addEventListener("ended", () => {
        const card = video.closest(CARD_SEL);
        restorePosterAttr(video);
        resetToPoster(video);
        setCardPlaying(card, false);
      });
    });

    const io = new IntersectionObserver(
      (entries) => entries.forEach((entry) => { if (!entry.isIntersecting) pauseAll(); }),
      { threshold: 0.01 }
    );
    io.observe(sectionRoot);

    mq.addEventListener?.("change", pauseAll);
  }

  function init() {
    // 1) athletes (FIX: ignoreInteractive true so desktop click works)
    document.querySelectorAll("[data-k-athletes-carousel]").forEach((carousel) => {
      const track = carousel.querySelector("[data-k-athletes-track]");
      if (!track) return;

      const dragState = attachDragScroll(track, {
        clickGuardMode: "all",
        ignoreInteractive: true, // ✅ key fix
      });

      attachAthletesVideo(carousel, track, dragState);
    });

    // 2) committee
    document.querySelectorAll("[data-k-committee]").forEach((section) => {
      const track = section.querySelector("[data-k-committee-track]");
      if (!track) return;

      const dragState = attachDragScroll(track, {
        clickGuardMode: "links",
        ignoreInteractive: true,
      });

      attachCommitteeVideo(section, track, dragState);
    });

    // 3) ingredients
    document.querySelectorAll("[data-k-ingredients-track]").forEach((track) => {
      attachDragScroll(track, { clickGuardMode: "all", ignoreInteractive: false });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
  document.addEventListener("shopify:section:select", init);
})();
